import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { cn } from './lib/utils';

const getApiConfig = () => ({
  url: process.env.REACT_APP_API_URL,
  auth: {
    username: process.env.REACT_APP_API_USER || '',
    password: process.env.REACT_APP_API_PASS || '',
  },
});

const MessageList = ({ theme = 'dark', onToggleTheme = () => {} }) => {
  const [messages, setMessages] = useState([]);
  const [searchContent, setSearchContent] = useState('');
  const [searchUserName, setSearchUserName] = useState('');
  const [createTimeFuzzy, setCreateTimeFuzzy] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const isFiltered = Boolean(searchContent || searchUserName || createTimeFuzzy || startTime || endTime);

  const fetchMessages = useCallback(() => {
    setLoading(true);
    setError('');

    const { url, auth } = getApiConfig();

    if (!url) {
      setError('请在 .env.local 中配置 REACT_APP_API_URL（以及 REACT_APP_API_USER/PASS）。');
      setLoading(false);
      return;
    }

    const params = {
      pageNum,
      pageSize,
    };

    if (searchContent) params.content = searchContent;
    if (searchUserName) params.userName = searchUserName;
    if (createTimeFuzzy) params.createTimeFuzzy = createTimeFuzzy;
    if (startTime) params.startTime = startTime;
    if (endTime) params.endTime = endTime;

    const authConfig = auth.username && auth.password ? { auth } : {};

    axios
      .get(url, {
        params,
        ...authConfig,
      })
      .then((response) => {
        if (!response.data || response.status === 204) {
          setMessages([]);
          setTotal(0);
        } else if (Array.isArray(response.data.list)) {
          setMessages(response.data.list);
          setTotal(response.data.total || response.data.list.length);
        } else {
          setMessages([]);
          setTotal(0);
        }
        setLastUpdated(new Date().toISOString());
        setError('');
      })
      .catch((error) => {
        console.error('There was an error fetching the messages!', error);
        setError('加载失败，请检查网络或稍后重试。');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pageNum, pageSize, searchContent, searchUserName, createTimeFuzzy, startTime, endTime]);

  useEffect(() => {
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }
    fetchDebounceRef.current = setTimeout(() => {
      fetchMessages();
    }, 350);

    return () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
      }
    };
  }, [pageNum, pageSize, searchContent, searchUserName, createTimeFuzzy, startTime, endTime, fetchMessages]);

  const handlePageChange = (page) => {
    setPageNum(Math.max(1, Math.min(page, totalPages)));
  };

  const handleSizeChange = (size) => {
    setPageSize(size);
    setPageNum(1);
  };

  const handleResetFilters = () => {
    setSearchContent('');
    setSearchUserName('');
    setCreateTimeFuzzy('');
    setStartTime('');
    setEndTime('');
    setPageNum(1);
  };

  const isDark = theme === 'dark';

  const formatTime = (value) => {
    if (!value) return '—';
    return moment(value).format('YYYY-MM-DD HH:mm:ss');
  };

  const formatLiveOffset = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const totalSeconds = Number(value);
    if (!Number.isFinite(totalSeconds)) return '—';
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    const pad = (num) => String(num).padStart(2, '0');
    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(minutes)}:${pad(seconds)}`;
  };

  const formatContentPreview = (text = '') => {
    if (!text) return '—';
    if (text.length <= 80) return text;
    const chunks = text.match(/.{1,56}/g);
    return chunks ? chunks.join('\n') : text;
  };

  const applyQuickRange = (preset) => {
    const now = moment();
    let start = '';
    let end = '';

    if (preset === 'today') {
      start = now.startOf('day').format('YYYY-MM-DDTHH:mm');
      end = now.endOf('day').format('YYYY-MM-DDTHH:mm');
    } else if (preset === 'week') {
      start = now.startOf('week').format('YYYY-MM-DDTHH:mm');
      end = now.endOf('week').format('YYYY-MM-DDTHH:mm');
    } else if (preset === 'month') {
      start = now.startOf('month').format('YYYY-MM-DDTHH:mm');
      end = now.endOf('month').format('YYYY-MM-DDTHH:mm');
    } else if (preset === 'clear') {
      start = '';
      end = '';
    }

    setStartTime(start);
    setEndTime(end);
    setPageNum(1);
  };

  const stats = useMemo(
    () => [
      { label: '总消息', value: total, hint: '接口返回 total' },
      { label: '当前页', value: `${pageNum} / ${totalPages}`, hint: '分页位置' },
      { label: '每页', value: `${pageSize} 条`, hint: '调整页容量' },
      { label: '筛选中', value: isFiltered ? 'ON' : 'OFF', hint: isFiltered ? '已应用筛选' : '未应用筛选' },
    ],
    [total, pageNum, totalPages, pageSize, isFiltered]
  );

  const activeFilters = [
    { label: '内容', value: searchContent, clear: () => setSearchContent('') },
    { label: '用户', value: searchUserName, clear: () => setSearchUserName('') },
    { label: '模糊时间', value: createTimeFuzzy, clear: () => setCreateTimeFuzzy('') },
    { label: '开始', value: startTime, clear: () => setStartTime('') },
    { label: '结束', value: endTime, clear: () => setEndTime('') },
  ].filter((item) => Boolean(item.value));

  const openDatePicker = (ref) => {
    if (ref?.current) {
      try {
        if (typeof ref.current.showPicker === 'function') {
          ref.current.showPicker();
        } else {
          ref.current.focus();
        }
      } catch (error) {
        ref.current.focus();
      }
    }
  };

  return (
    <div className={cn('space-y-8', isDark ? 'text-foreground' : 'text-slate-900')}>
      <header
        className={cn(
          'relative overflow-hidden rounded-2xl border p-4 shadow-lg backdrop-blur sm:p-6',
          isDark
            ? 'border-white/10 bg-slate-900/70 shadow-indigo-900/20'
            : 'border-slate-200 bg-white shadow-slate-200/60'
        )}
      >
        <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <h1 className={cn('text-3xl font-semibold tracking-tight sm:text-4xl', isDark ? 'text-white' : 'text-slate-900')}>
              实时消息列表
            </h1>
            <p className={cn('text-sm', isDark ? 'text-slate-200' : 'text-slate-600')}>
              查看实时消息、筛选用户与时间范围，支持快速分页与主题切换。
            </p>
            <div className="flex flex-wrap gap-3">
              {stats
                .filter((stat) => stat.label !== '总消息')
                .map((stat) => (
                  <Badge
                    key={stat.label}
                    variant="outline"
                    className={cn(
                      'gap-2 whitespace-nowrap',
                      isDark ? 'border-white/20 bg-white/5 text-slate-100' : 'border-slate-200 bg-slate-50 text-slate-800'
                    )}
                  >
                    <span className="text-xs uppercase tracking-wide">{stat.label}</span>
                    <span className="text-sm font-semibold">{stat.value}</span>
                  </Badge>
                ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <Badge
              variant="outline"
              className={cn(
                isDark ? 'border-white/20 bg-slate-900/80 text-slate-100' : 'border-slate-300 bg-white text-slate-800'
              )}
            >
              总数 · {total}
            </Badge>
            <Button
              variant="outline"
              onClick={onToggleTheme}
              className={cn(
                'text-sm',
                isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
              )}
            >
              {isDark ? '🌞 亮色' : '🌜 暗色'}
            </Button>
            <div className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}>
              {lastUpdated ? `上次更新：${moment(lastUpdated).format('HH:mm:ss')}` : '等待首次加载…'}
            </div>
          </div>
        </div>
      </header>

      {error && (
        <div
          className={cn(
            'flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
            isDark ? 'border-red-400/30 bg-red-500/10 text-red-100' : 'border-red-200 bg-red-50 text-red-700'
          )}
          role="alert"
          aria-live="assertive"
        >
          <span>{error}</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchMessages}
              className={cn(
                isDark ? 'border-red-200/50 text-red-100 hover:bg-red-500/10' : 'border-red-200 text-red-700 hover:bg-red-100'
              )}
            >
              重试
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setError('')}
              className={cn(isDark ? 'text-red-100 hover:bg-white/10' : 'text-red-700 hover:bg-red-100')}
            >
              知道了
            </Button>
          </div>
        </div>
      )}

      <Card
        className={cn(
          'backdrop-blur',
          isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white/90 shadow-xl shadow-slate-200/60'
        )}
      >
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>筛选器</CardTitle>
            <CardDescription className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              输入条件后自动刷新；支持内容、用户、模糊时间和时间范围。
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'sm:hidden',
              isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
            )}
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            {filtersOpen ? '收起筛选' : '展开筛选'}
          </Button>
        </CardHeader>
        <CardContent className={cn('space-y-6', filtersOpen ? 'block' : 'hidden sm:block')}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="content" className={isDark ? 'text-slate-100' : 'text-slate-800'}>
                内容搜索
              </Label>
              <Input
                id="content"
                aria-label="内容搜索"
                placeholder="例如：优惠、直播"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
                className={cn(
                  'placeholder:text-slate-500',
                  isDark ? 'bg-slate-950/60 text-white' : 'bg-white text-slate-900 border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user" className={isDark ? 'text-slate-100' : 'text-slate-800'}>
                用户名
              </Label>
              <Input
                id="user"
                aria-label="用户名搜索"
                placeholder="输入用户名"
                value={searchUserName}
                onChange={(e) => setSearchUserName(e.target.value)}
                className={cn(
                  'placeholder:text-slate-500',
                  isDark ? 'bg-slate-950/60 text-white' : 'bg-white text-slate-900 border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fuzzy" className={isDark ? 'text-slate-100' : 'text-slate-800'}>
                时间模糊搜索
              </Label>
              <Input
                id="fuzzy"
                aria-label="时间模糊搜索"
                placeholder="2024 或 2024-10"
                value={createTimeFuzzy}
                onChange={(e) => setCreateTimeFuzzy(e.target.value)}
                className={cn(
                  'placeholder:text-slate-500',
                  isDark ? 'bg-slate-950/60 text-white' : 'bg-white text-slate-900 border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start" className={isDark ? 'text-slate-100' : 'text-slate-800'}>
                开始时间
              </Label>
              <Input
                id="start"
                type="datetime-local"
                aria-label="开始时间"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                onClick={() => openDatePicker(startInputRef)}
                onFocus={() => openDatePicker(startInputRef)}
                ref={startInputRef}
                className={cn(
                  'placeholder:text-slate-500',
                  isDark ? 'bg-slate-950/60 text-white' : 'bg-white text-slate-900 border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end" className={isDark ? 'text-slate-100' : 'text-slate-800'}>
                结束时间
              </Label>
              <Input
                id="end"
                type="datetime-local"
                aria-label="结束时间"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                onClick={() => openDatePicker(endInputRef)}
                onFocus={() => openDatePicker(endInputRef)}
                ref={endInputRef}
                className={cn(
                  'placeholder:text-slate-500',
                  isDark ? 'bg-slate-950/60 text-white' : 'bg-white text-slate-900 border-slate-200'
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-slate-300' : 'text-slate-600')}>
              快捷时间
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyQuickRange('today')}
              className={cn(
                'text-sm',
                isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
              )}
            >
              今日
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyQuickRange('week')}
              className={cn(
                'text-sm',
                isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
              )}
            >
              本周
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => applyQuickRange('month')}
              className={cn(
                'text-sm',
                isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
              )}
            >
              本月
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => applyQuickRange('clear')}
              className={cn('text-sm', isDark ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100')}
            >
              清空时间
            </Button>
          </div>

          <div
            className={cn(
              'flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between',
              isDark ? 'border-white/10' : 'border-slate-200'
            )}
          >
            <p className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}>
              条件会实时应用。若接口有延迟，可点击刷新。重置会清空筛选并返回第一页。
            </p>
            <div className="flex items-center gap-2">
              {isFiltered && (
                <Button
                  variant="ghost"
                  onClick={handleResetFilters}
                  className={cn(isDark ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100')}
                  disabled={loading}
                >
                  清空筛选
                </Button>
              )}
              <Button
                variant="outline"
                onClick={fetchMessages}
                disabled={loading}
                className={cn(
                  isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
                )}
              >
                {loading ? '刷新中...' : '立即刷新'}
              </Button>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div
              className={cn(
                'flex flex-wrap items-center gap-2 rounded-xl p-3',
                isDark ? 'border border-white/10 bg-slate-950/60' : 'border border-slate-200 bg-white'
              )}
            >
              <span
                className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-slate-300' : 'text-slate-600')}
              >
                已应用
              </span>
              {activeFilters.map((filter) => (
                <Button
                  key={filter.label}
                  size="sm"
                  variant="ghost"
                  className={cn(
                    'gap-2 rounded-full border',
                    isDark
                      ? 'border-white/10 bg-white/5 text-slate-100 hover:bg-white/15'
                      : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100'
                  )}
                  onClick={filter.clear}
                  disabled={loading}
                >
                  <span className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-500')}>{filter.label}</span>
                  <span className={cn('font-medium', isDark ? 'text-white' : 'text-slate-900')}>{filter.value}</span>
                  <span className={cn(isDark ? 'text-slate-300' : 'text-slate-500')}>✕</span>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card
        className={cn(
          'backdrop-blur',
          isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white/90 shadow-xl shadow-slate-200/60'
        )}
      >
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>消息列表</CardTitle>
            <CardDescription className={isDark ? 'text-slate-300' : 'text-slate-600'}>
              {loading ? '正在加载数据…' : `第 ${pageNum} / ${totalPages} 页 · 共 ${total} 条`}
            </CardDescription>
          </div>
          <Badge
            variant="secondary"
            className={cn(isDark ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-800 border-slate-200')}
          >
            每页 {pageSize} 条
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div
              className={cn(
                'hidden overflow-hidden rounded-xl border shadow-[0_20px_80px_-28px_rgba(59,130,246,0.35)] md:block',
                isDark ? 'border-white/10 bg-slate-950/70' : 'border-slate-200 bg-white'
              )}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-700'}>创建时间</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-700'}>回放点</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-700'}>用户名</TableHead>
                    <TableHead className={isDark ? 'text-slate-200' : 'text-slate-700'}>消息内容</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, idx) => (
                      <TableRow key={`skeleton-${idx}`} className="animate-pulse">
                        <TableCell>
                          <div className={cn('h-4 w-32 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                        </TableCell>
                        <TableCell>
                          <div className={cn('h-4 w-16 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                        </TableCell>
                        <TableCell>
                          <div className={cn('h-4 w-24 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                        </TableCell>
                        <TableCell>
                          <div className={cn('h-4 w-full rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : messages.length ? (
                    messages.map((message, index) => (
                      <TableRow
                        key={`${message.createTime}-${index}`}
                        className={cn(
                          'transition-colors',
                          isDark
                            ? 'odd:bg-white/[0.02] even:bg-slate-950/60 hover:bg-white/5'
                            : 'odd:bg-slate-50/60 even:bg-white hover:bg-slate-50'
                        )}
                      >
                        <TableCell
                          className={cn(
                            'whitespace-nowrap font-medium',
                            isDark ? 'text-primary' : 'text-indigo-600'
                          )}
                        >
                          {formatTime(message.createTime)}
                        </TableCell>
                        <TableCell className={cn('whitespace-nowrap', isDark ? 'text-slate-100' : 'text-slate-800')}>
                          {formatLiveOffset(message.secondsSinceLiveStart)}
                        </TableCell>
                        <TableCell className={cn('whitespace-nowrap', isDark ? 'text-slate-100' : 'text-slate-800')}>
                          {message.userName || '未知用户'}
                        </TableCell>
                        <TableCell className={cn(isDark ? 'text-slate-200' : 'text-slate-700')}>{message.content}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className={cn('py-8 text-center text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}
                      >
                        暂无数据，调整筛选条件或点击刷新重试。
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {loading
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <div
                      key={`mobile-skeleton-${idx}`}
                      className={cn(
                        'space-y-2 rounded-xl border p-4',
                        isDark ? 'border-white/10 bg-slate-950/70' : 'border-slate-200 bg-white',
                        'animate-pulse'
                      )}
                    >
                      <div className={cn('h-4 w-32 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                      <div className={cn('h-4 w-24 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                      <div className={cn('h-4 w-20 rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                      <div className={cn('h-4 w-full rounded', isDark ? 'bg-white/10' : 'bg-slate-200')} />
                    </div>
                  ))
                  : messages.length
                  ? messages.map((message, index) => (
                      <div
                        key={`${message.createTime}-${index}`}
                        className={cn(
                          'space-y-2 rounded-xl border p-4',
                          isDark ? 'border-white/10 bg-slate-950/70' : 'border-slate-200 bg-white'
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('text-xs font-semibold', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            时间
                          </span>
                          <span className={cn('text-sm font-medium', isDark ? 'text-primary' : 'text-indigo-600')}>
                            {formatTime(message.createTime)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('text-xs font-semibold', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            回放点
                          </span>
                          <span className={cn('text-sm font-medium', isDark ? 'text-slate-100' : 'text-slate-800')}>
                            {formatLiveOffset(message.secondsSinceLiveStart)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('text-xs font-semibold', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            用户
                          </span>
                          <span className={cn('text-sm font-medium', isDark ? 'text-slate-100' : 'text-slate-800')}>
                            {message.userName || '未知用户'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn('text-xs font-semibold', isDark ? 'text-slate-300' : 'text-slate-600')}>
                            内容
                          </span>
                          <p
                            className={cn(
                              'ml-auto max-w-[75%] whitespace-pre-wrap break-words rounded-lg border p-2 text-sm leading-relaxed text-right',
                              isDark
                                ? 'border-white/10 bg-white/5 text-slate-200'
                                : 'border-slate-200 bg-slate-50 text-slate-700'
                            )}
                          >
                            {formatContentPreview(message.content)}
                          </p>
                        </div>
                      </div>
                    ))
                : (
                    <div
                      className={cn(
                        'rounded-xl border p-4 text-center text-sm',
                        isDark ? 'border-white/10 bg-slate-950/70 text-slate-300' : 'border-slate-200 bg-white text-slate-600'
                      )}
                    >
                      暂无数据，调整筛选条件或点击刷新重试。
                    </div>
                  )}
            </div>
          </div>

          <div
            className={cn(
              'flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between',
              isDark ? 'border-white/10' : 'border-slate-200'
            )}
          >
            <div className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
              共 {total} 条 · 第 {pageNum} 页 / {totalPages} 页
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pageNum - 1)}
                disabled={pageNum === 1 || loading}
                className={cn(
                  isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
                )}
              >
                上一页
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pageNum + 1)}
                disabled={pageNum >= totalPages || loading}
                className={cn(
                  isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-200 text-slate-800 hover:bg-slate-100'
                )}
              >
                下一页
              </Button>
              <select
                aria-label="分页大小"
                className={cn(
                  'h-10 rounded-md px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring',
                  isDark ? 'border-white/20 bg-slate-950/70 text-slate-100' : 'border-slate-200 bg-white text-slate-800'
                )}
                value={pageSize}
                onChange={(e) => handleSizeChange(Number(e.target.value))}
                disabled={loading}
              >
                <option value={10}>10 条/页</option>
                <option value={20}>20 条/页</option>
                <option value={50}>50 条/页</option>
                <option value={100}>100 条/页</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MessageList;
